import { apiClient } from "@/lib/apiClient"

/**
 * Backend auth endpoint'leri (euas-backend ile uyumlu)
 * Base: /api/auth
 */
const ENDPOINTS = {
    /** Yönetici ve çalışan için aynı LDAP login endpoint */
    LOGIN_LDAP: "/api/auth/login/ldap",
    STAFF_OTP_REQUEST: "/api/auth/login/otp/request",
    STAFF_OTP_VERIFY: "/api/auth/login/otp/verify",
    REFRESH: "/api/auth/refresh",
    LOGOUT: "/api/auth/logout",
    DOMAIN: "/api/domain/public",
    SELECT_COMPANY: "/api/staff-portal/switch",
    FORGOT_PASSWORD_EMAIL: "/api/auth/forgot-password-email",
    RESET_PASSWORD_EMAIL: "/api/auth/reset-password-email",
    VERIFY_PHONE: "/api/auth/verify-phone",
    RESEND_VERIFICATION_OTP: "/api/auth/resend-verification-otp",
} as const

/**
 * Türkiye telefon numarasını E.164 formatına çevirir (+905XXXXXXXXX)
 */
export const toE164Phone = (phone: string): string => {
    const digits = phone.replace(/\D/g, "")
    if (digits.length === 10 && digits.startsWith("5")) {
        return `+90${digits}`
    }
    if (digits.length === 11 && digits.startsWith("90")) {
        return `+${digits}`
    }
    if (digits.startsWith("+90") || digits.startsWith("90")) {
        return digits.startsWith("+") ? digits : `+${digits}`
    }
    return phone.startsWith("+") ? phone : `+90${digits}`
}

export const authApi = {
    /**
     * LDAP Login - Hem yönetici hem çalışan için aynı endpoint
     * POST /api/auth/login/ldap { username, password }
     */
    login: async (data: { username: string; password: string }) => {
        const response = await apiClient.post(ENDPOINTS.LOGIN_LDAP, {
            username: data.username,
            password: data.password,
        })
        return response.data
    },

    /**
     * Staff login - OTP talep et (opsiyonel, LDAP kullanılıyorsa gerekmez)
     */
    staffLogin: async (data: { identifier: string; domainID: string }) => {
        const phoneNumber = toE164Phone(data.identifier)
        const response = await apiClient.post(ENDPOINTS.STAFF_OTP_REQUEST, { phoneNumber })
        return response.data
    },

    /**
     * Staff login - OTP doğrula ve token al
     */
    verifyOTP: async (data: { domainID: string; identifier: string; otpCode: string }) => {
        const phoneNumber = toE164Phone(data.identifier)
        const response = await apiClient.post(ENDPOINTS.STAFF_OTP_VERIFY, {
            phoneNumber,
            code: data.otpCode,
        })
        return response.data
    },

    /**
     * OTP tekrar gönder (staff login için - aynı request endpoint kullanılır)
     */
    resendOTP: async (data: { phone: string; domainID: string }) => {
        const phoneNumber = toE164Phone(data.phone)
        const response = await apiClient.post(ENDPOINTS.STAFF_OTP_REQUEST, { phoneNumber })
        return response.data
    },

    /**
     * Profil telefon doğrulama OTP'si için
     */
    resendVerificationOtp: async (data: { phone: string }) => {
        const phoneNumber = toE164Phone(data.phone)
        const response = await apiClient.post(ENDPOINTS.RESEND_VERIFICATION_OTP, { phoneNumber })
        return response.data
    },

    /**
     * Token yenile
     */
    refresh: async (refreshToken?: string) => {
        const body = refreshToken ? { refreshToken } : {}
        const response = await apiClient.post(ENDPOINTS.REFRESH, body, {
            skipErrorToast: true,
        })
        return response.data
    },

    /**
     * Çıkış
     */
    logout: async (refreshToken?: string) => {
        const body = refreshToken ? { refreshToken } : {}
        const response = await apiClient.post(ENDPOINTS.LOGOUT, body, {
            skipErrorToast: true,
        })
        return response.data
    },

    /**
     * Domain bilgisi (public)
     */
    getDomain: async (domainName: string) => {
        const response = await apiClient.get(`${ENDPOINTS.DOMAIN}/${domainName}`)
        return response.data
    },

    /**
     * Şirket/domain seç (staff portal)
     */
    selectCompany: async (company: { key: string }) => {
        const params = new URLSearchParams({ domain: company.key })
        const response = await apiClient.get(`${ENDPOINTS.SELECT_COMPANY}?${params}`)
        return response.data
    },

    /**
     * Şifremi unuttum - email ile OTP talep
     */
    forgotPassword: async (data: { email: string }) => {
        const response = await apiClient.post(ENDPOINTS.FORGOT_PASSWORD_EMAIL, { email: data.email })
        return response.data
    },

    /**
     * Şifre sıfırla - email + OTP kodu + yeni şifre
     */
    resetPassword: async (data: {
        email: string
        code: string
        password: string
        passwordConfirm: string
    }) => {
        const response = await apiClient.post(ENDPOINTS.RESET_PASSWORD_EMAIL, {
            email: data.email,
            code: data.code,
            newPassword: data.password,
        })
        return response.data
    },

    /**
     * Telefon doğrulama (profil için)
     */
    verifyOTPForPasswordReset: async (_data: { phone: string; otpCode: string; domainID: string }) => {
        // Backend'de email-based reset kullanılıyor. Bu endpoint artık resetPassword ile birleştirildi.
        throw new Error("Password reset uses email flow. Use resetPassword with email and code.")
    },

    verifyPhone: async (data: { phoneNumber: string; code: string }) => {
        const phoneNumber = toE164Phone(data.phoneNumber)
        const response = await apiClient.post(ENDPOINTS.VERIFY_PHONE, { phoneNumber, code: data.code })
        return response.data
    },
}
